<script lang="ts">
  import { Doc, Edit } from "$components/+blocks/OVIcon";
  import { sidePanel } from "app/stores/sidePanel";
  import { fade } from "svelte/transition";
  import { onDestroy } from "svelte";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import { DocumentTypeEnum } from "../../models/documents";
  import Button from "$components/+blocks/Button/Button.svelte";
  import Composer from "$components/editor/Composer.svelte";
  import Reader from "../editor/Reader.svelte";
  import { getMediaQuery } from "app/utils/mediaQuery";
  import cx from "classnames";
  import type { JSONContent } from "@tiptap/core";
  import type { YdocStore } from "$components/editor";
  import type { UserStore } from "$stores/user";

  export let content: JSONContent;
  export let ydoc: YdocStore | null;
  export let user: UserStore;
  export let readOnly = true;
  export let docType: DocumentTypeEnum = DocumentTypeEnum.Document;
  export let canEdit = false;
  export let color = "";
  export let hasContentsBlock = false;
  export let onUpdate: (detail: any) => void;
  export let onSaveDraft: () => Promise<void>;
  export let reader: Reader | undefined = undefined;

  const DRAFT_TIMER_MILLIS = 5000;
  const isDesktop = getMediaQuery("desktop");
  let saveDraftTimer: NodeJS.Timeout;

  function handleUpdate(ev: CustomEvent) {
    if (!readOnly) {
      // save draft after a period of inactivity
      clearTimeout(saveDraftTimer);
      saveDraftTimer = setTimeout(() => {
        if (docType === DocumentTypeEnum.Document && !readOnly) {
          onSaveDraft().catch(() => {
            // just ignore any errors
          });
        }
      }, DRAFT_TIMER_MILLIS);

      onUpdate(ev.detail);
    }
  }

  onDestroy(() => clearInterval(saveDraftTimer));
</script>

<div
  class={cx("mx-auto h-fit rounded-xl border-[0.5px] p-8 transition-all duration-200", {
    "w-full max-w-[800px]": !hasContentsBlock,
    "w-fit": hasContentsBlock,
    "mt-4": docType !== DocumentTypeEnum.Snippet,
    "min-h-[calc(100%-16px)]": docType === DocumentTypeEnum.Document,
    "min-h-full rounded-t-[2px] rounded-br-[2px]": docType === DocumentTypeEnum.Snippet,
    "border-transparent bg-transparent": readOnly,
    "border-blue-600/70 bg-black/2 dark:border-blue-300/50 dark:bg-white/2": !readOnly,
  })}
>
  {#if !readOnly && $user.isAuthed}
    <Composer
      inlineMentions={docType !== DocumentTypeEnum.Snippet}
      advancedBlocks={docType !== DocumentTypeEnum.Snippet}
      embedsBlocks={docType !== DocumentTypeEnum.Snippet}
      forceHeading={docType === DocumentTypeEnum.Document || docType === DocumentTypeEnum.Snippet}
      editable={!readOnly}
      content={readOnly ? content : null}
      ydoc={!readOnly ? ydoc : null}
      highlightColor={color}
      on:update={handleUpdate}
      user={$user}
    />
    <slot name="footer" {hasContentsBlock} />
  {:else if content && Object.keys(content).length}
    <Reader
      {content}
      isDocument={docType === DocumentTypeEnum.Document || docType === DocumentTypeEnum.Snippet}
      disableLinksInSSR
      bind:this={reader}
    />
    <slot name="footer" {hasContentsBlock} />
  {:else if canEdit}
    <div class="mt-28 flex flex-col items-center" in:fade>
      <OVIcon icon={Doc} size="size-20" class="text-gray-300 dark:text-gray-400/20" />
      <div class="mt-4 font-medium text-gray-600 dark:text-gray-200">
        New {docType === DocumentTypeEnum.Snippet ? "snippet" : "document"}
      </div>
      <div
        class="mt-4 max-w-[400px] text-center text-sm font-medium text-gray-500/70 dark:text-gray-400"
      >
        Click below to start adding content
      </div>
      <div class="mt-6">
        <Button
          size="large"
          variant="primary"
          label="Start writing"
          iconRight={Edit}
          on:click={() => {
            readOnly = false;
            if ($isDesktop) sidePanel.show();
          }}
        />
      </div>
    </div>
  {/if}
</div>
